import { apiService } from './ApiService';

export interface AssistanceRequestCreate {
  order_id: number;
  session_id: string;
  subject: string;
  description: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  contact_preference?: 'email' | 'phone' | 'chat';
  contact_info?: string;
}

export interface AssistanceRequestResponse {
  id: number;
  uid: string;
  order_id: number;
  session_id: string;
  subject: string;
  description: string;
  priority: string;
  status: string;
  contact_preference?: string;
  contact_info?: string;
  created_at: string;
  updated_at: string;
}

export interface AssistanceRequestListResponse {
  status: string;
  requests: AssistanceRequestResponse[];
  total_count: number;
}

export interface ApplicationErrorReport {
  error_name: string;
  error_message: string;
  error_stack?: string;
  component_stack?: string;
  page_url: string;
  user_agent: string;
  occurred_at: string;
  app_version?: string;
  viewport?: string;
  language?: string;
}

export class AssistanceService {
  private static instance: AssistanceService;

  public static getInstance(): AssistanceService {
    if (!AssistanceService.instance) {
      AssistanceService.instance = new AssistanceService();
    }
    return AssistanceService.instance;
  }

  /**
   * Create a new assistance request
   */
  public async createAssistanceRequest(
    data: AssistanceRequestCreate
  ): Promise<AssistanceRequestResponse> {
    const response = await apiService.post<AssistanceRequestResponse>(
      '/api/user/assistance/',
      data
    );
    return response.data;
  }

  public async reportApplicationError(data: ApplicationErrorReport): Promise<void> {
    await apiService.post('/api/user/assistance/report-error', data);
  }

  /**
   * Get all assistance requests for the current user
   */
  public async getAssistanceRequests(
    status?: string,
    orderId?: number,
    limit: number = 50,
    offset: number = 0
  ): Promise<AssistanceRequestListResponse> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (orderId) params.append('order_id', orderId.toString());
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response = await apiService.get<AssistanceRequestListResponse>(
      `/api/user/assistance/?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Get a specific assistance request by ID
   */
  public async getAssistanceRequest(
    requestId: number
  ): Promise<AssistanceRequestResponse> {
    const response = await apiService.get<AssistanceRequestResponse>(
      `/api/user/assistance/${requestId}`
    );
    return response.data;
  }
}

// Export singleton instance
export const assistanceService = AssistanceService.getInstance();
